import React, { useEffect, useRef, useCallback } from "react";
import BaseModal from "@/shared/components/modal/base-modal";

interface Props {
  isShow: boolean;
  title: JSX.Element;
  children?: JSX.Element;
  leftButton?: JSX.Element;
  rightButton: JSX.Element;
  outAreaOnTap?: () => void;
  /** z-indexを指定する（デフォルト: undefined = CSSのデフォルト値を使用） */
  zIndex?: number;
  /** オーバーレイのカスタムID（デフォルト: "modalOverlay"） */
  overlayId?: string;
}

export const BaseDialog = (props: Props) => {
  const { isShow, title, children, leftButton, rightButton, outAreaOnTap, zIndex, overlayId } =
    props;
  const dialogRef = useRef<HTMLElement>(null);

  // ダイアログのフォーカストラップ
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    if (!dialogRef.current) return;

    const focusableElements = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => {
      if (["SECTION", "DIV", "ASIDE"].includes(el.tagName)) return false;
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden";
    });
    if (focusableElements.length === 0) return;

    e.preventDefault();
    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    let nextIndex: number;
    if (currentIndex === -1) {
      nextIndex = e.shiftKey ? focusableElements.length - 1 : 0;
    } else if (e.shiftKey) {
      nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
    }
    focusableElements[nextIndex].focus();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <aside
      ref={dialogRef}
      key="dialogAside"
      id="dialog"
      className="dialog-container"
    >
      <BaseModal isShow={isShow} outAreaOnTap={outAreaOnTap} zIndex={zIndex} overlayId={overlayId}>
        <div className="base-dialog">
          <div className="base-dialog__header">{title}</div>
          {children}
          <div className="base-dialog__footer">
            {leftButton}
            {rightButton}
          </div>
        </div>
      </BaseModal>
    </aside>
  );
};

export default BaseDialog;
